import { useEagerLogoLoading } from "@insite/client-framework/Common/EagerLoadingLogo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getHomePageUrl } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Img, { ImgProps } from "@insite/mobius/Img";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getFeatureFlag } from "@MasterBluePrint/Services/LaunchDarklyService";
import { getLaunchDarklySettings } from "@MasterBluePrint/Store/Context/ContextSelector";
import { LaunchDarklySettingsModel } from "@MasterBluePrint/Types/ApiModels";
import * as React from "react";
import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const homePageLink = getHomePageUrl(state);
    const launchDarklySettings = getLaunchDarklySettings(state);
    return {
        homePageLink,
        CustomSettings: launchDarklySettings,
    }
};

const enum fields {
    logoImage = "logoImage",
    isMobileSpecific = "isMobileSpecific",
    mobileSpecificImage = "mobileSpecificImage",
    //  logoBesideText = "logoBesideText",
    displaySVG = "displaySVG",
    showLogoText = "showLogoText",
    svgHTML = "svgHTML",
    logoAriaLabel = "logoAriaLabel",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.logoImage]: string;
        [fields.isMobileSpecific]: boolean;
        [fields.mobileSpecificImage]: string;
        // [fields.logoBesideText]: string;
        [fields.displaySVG]: boolean;
        [fields.showLogoText]: boolean;
        [fields.svgHTML]: string;
        [fields.logoAriaLabel]: string;
    };
    extendedStyles?: LogoStyles;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface LogoStyles {
    wrapper?: InjectableCss;
    logo?: InjectableCss;
    /**
     * @deprecated Use img instead
     */
    image?: LazyImageProps;
    img?: ImgProps;
    logoTextHidden: HiddenProps;
    logoTextWrapper: InjectableCss;
}

export const logoStyles: LogoStyles = {
    wrapper: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    logo: {
        css: css`
        height: 2.7rem;
        width: 11rem;
        position: relative;
            svg {
                display: block;
                vertical-align: middle;
                height: 2.7rem;
                width: 100%;
                margin-left: auto;
                margin-right: auto;
            }
            @media screen and (max-width: 1080px) and (min-width: 1024px) {
                top: 0;
            }
            @media (max-width: 767px) {
                max-height: 1rem;
                width: auto;
                padding: .9375rem;
                top: -.875rem;
                right: .938rem;
            }
        `,
    },
    image: {
        css: css`
            max-width: 6.875rem;
            max-height: 6.875rem;
            width: 100%;
        `,
    },
    img: {
        css: css`
            width: 100%;
        `,
    },
    logoTextHidden: {
        
    },
    logoTextWrapper: {
        css: css`
            margin: 0 0 .18750625rem .9375rem;
            opacity: .78;
            color: #333f48;
            font-family: 'Lato', sans-serif;
            font-size: .9rem;
            line-height: 1rem;
            text-transform: initial;
            font-weight: 400 !important;
            letter-spacing: .02rem;
            @media (max-width: 767px) {
                margin: 1rem 0 0;
                max-width: 49%;
                padding-right: 1rem;
            }
        `,
    },
};

export const Logo: React.FunctionComponent<Props> = ({ fields, extendedStyles, homePageLink, CustomSettings }: Props) => {
    const [isFeatureOn, setIsFeatureOn] = useState(false);

    const featureFlagKey = "TEEU-44"

    useEffect(() => {
        const checkFeatureFlag = async (customSettings: LaunchDarklySettingsModel, flagKey: string) => {
            const flagValue = await getFeatureFlag(customSettings, flagKey);
            setIsFeatureOn(flagValue);
        }
        checkFeatureFlag(CustomSettings,featureFlagKey);
    }, [featureFlagKey])

    const styles = useMergeStyles("logo", logoStyles, extendedStyles);
    const isEager = useEagerLogoLoading();
    const svgHTML = fields.svgHTML;
    const showLogoText = fields.showLogoText;
    const displaySVGLogo = fields.displaySVG;

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.logo}>
                {!displaySVGLogo && (
                    <Link href={homePageLink} aria-label={fields.logoAriaLabel}>
                        <Img
                            src={fields.logoImage}
                            loading={isEager ? "eager" : "lazy"}
                            altText={translate("Home")}
                            {...styles.img}
                        />
                    </Link>
                )}
                {displaySVGLogo && (
                    <Link href={homePageLink} aria-label={fields.logoAriaLabel}>
                        <StyledWrapper dangerouslySetInnerHTML={{ __html: svgHTML }}></StyledWrapper>
                    </Link>
                )}
            </StyledWrapper>
            {showLogoText && (
                <StyledWrapper {...styles.logoTextWrapper}>
                    <Hidden {...styles.logoTextHidden}>
                        {siteMessage("SiteHeader_LogoText")}
                    </Hidden>
                </StyledWrapper>
            )}
            {isFeatureOn && <h1 style={{ fontWeight: "bold", color: "blue" }}>This is a sample launchDarkly text</h1>}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(Logo),
    definition: {
        group: "Basic",
        icon: "Logo",
        fieldDefinitions: [
            {
                name: fields.logoImage,
                displayName: "Logo Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.isMobileSpecific,
                displayName: "Mobile Specific Image",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isRequired: true,
                variant: "toggle",
            },
            {
                name: fields.mobileSpecificImage,
                displayName: "Mobile Logo",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "Translatable",
                isVisible: widget => widget.fields.isMobileSpecific,
            },
            // {
            //     name: fields.logoBesideText,
            //     displayName: "Test Beside Logo",
            //     editorTemplate: "TextField",
            //     defaultValue: "",
            //     isRequired: false,
            //     fieldType: "General",
            // },
            {
                name: fields.displaySVG,
                displayName: "Display svg as logo",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.showLogoText,
                displayName: "Show Logo Text",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isVisible: widget => widget.fields.displaySVG,
            },
            {
                name: fields.svgHTML,
                displayName: "Svg HTML code",
                editorTemplate: "TextField",
                defaultValue: "",
                isRequired: false,
                fieldType: "General",
                isVisible: widget => widget.fields.displaySVG,
            },
            {
                name: fields.logoAriaLabel,
                displayName: "Logo Aria Label",
                editorTemplate: "TextField",
                defaultValue: "",
                isRequired: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
